// eslint-disable-next-line
import { makeObservable, observable, runInAction } from "mobx";
import PagedDataStore, { IFetchListArgs } from "../../../../stores/abstract-paged-data-store";
import RmaListItemStore from "./rma-item-store";
import RmaListService, { IFetchRmaListArgs } from "../../../../services/api/rma-list-service";
import { TYPE, rootContainer } from "../../../../services/container/root";
import RmaListFiltersStore from "./rma-list-filters-store";
import rmaPageMainStore from "../../rma-page/stores/rma-page-main-store";
import { IIframeService } from "../../../../services/iframe-service/iframe-service";


export enum RMA_QUICK_FILTERS {
    ALL = 'all',
    OPEN = 'open',
    PENDING = 'pending',
    APPROVED = 'approved',
    IN_TRANSIT = 'in-transit',
    RECEIVED = 'received',
    DONE = 'done',
    REJECTED = 'rejected',
    CANCELED = 'canceled',
    SHIPMENT_REJECTED = 'shipment_rejected',
    REQUIRE_ATTENTION = 'require-attention',
    PENDING_MANAGER_APPROVAL_ONLY = 'pending-manager-approval-only',
    PENDING_APPROVAL_ONLY = 'pending-approval-only',
}

class RmaPagedListStore extends PagedDataStore<RmaListItemStore> {
    @observable private _rmaListFiltersStore: RmaListFiltersStore = new RmaListFiltersStore();

    constructor() {
        super();
        makeObservable(this);
    }
    readonly perPage: number = 20;
    protected get _rmaListService(): RmaListService { return rootContainer.get<RmaListService>(TYPE.RmaListService); }
    @observable private _isLoaded: boolean = false;

    get rmaListFiltersStore() {
        return this._rmaListFiltersStore;
    }

	private get iframeService(): IIframeService {
		return rootContainer.get<IIframeService>(TYPE.IframService);
	}

    protected async fetchList(args: IFetchListArgs) {
        try {
            // One of 3 conditions should be true in order to fetch the list:
            //  - list has not ever been loaded - initial loading
            //  - filters were modified
            //  - some rmas were modified (for example inside RMA page)
            if (this._isLoaded && !this._rmaListFiltersStore.hasChanges() && !rmaPageMainStore.getUpdatedRmas()) {
                return;
            }
            const filters = this._rmaListFiltersStore.getUpdatedModel();
            const rmaListArgs: IFetchRmaListArgs = { ...args, ...filters } as IFetchRmaListArgs;
            const result = await this._rmaListService.fetchRmaList(rmaListArgs);
            this._rmaListFiltersStore.commitChanges();
            runInAction(() => {
                this._isLoaded = true;
            });

            return {
                ...result,
                data: result.data.map(rma => {
                    const store = new RmaListItemStore(rma);
                    return store;
                })
            };
        }
        catch (err) {
            console.error(err);
        }

    }

    public async load() {
        /** handle top frame history changes */
        this.iframeService.navigateReturns({});

        /** if we are navigating from RMA page to the list and the list was not loaded - load and exit */
        if (!this._isLoaded) {
            await this.loadPage(this._page);
            return;
        }

        /** if list was rendered and then some rmas were updated before user navigated back to the list - reload it  */
        const updatedRmaIds = rmaPageMainStore.getUpdatedRmas();
        if (updatedRmaIds.length > 0) {
            await this.loadPage(this._page);
            rmaPageMainStore.clearUpdatedRmaIds();
        }
    }

    public resetIsLoaded() {
        this._isLoaded = false;
    }
}

export default new RmaPagedListStore();
